import { ProjectCompany } from '@mercator-gcp/types/backend/CompanyInProject';
import { useQuery } from 'react-query';
import { useDocumentActivitySummary } from 'hooks/useProjectActivitySummary';
import { useDocumentLandTitleValue } from 'hooks/useProjectLandTitleValue';
import { useProjectsByAddress } from 'hooks/useProjectsByAddress';
import DocumentsAPI from 'services/API/DocumentsAPI';
import { DocumentOverview } from 'types/TempDocument';
import { dedupeLandTitleChangeEvents } from 'utils/activity';

export const useDocumentDetailsForDDP = (documentId: string) => {
  const { data: project, isLoading: isLoadingProject } = useQuery(
    ['get-document-overview', documentId],
    async (): Promise<DocumentOverview | null> => {
      const result = await DocumentsAPI.getDocumentOverview(documentId);

      return result.data;
    },
    { refetchOnWindowFocus: false },
  );

  const {
    isLoading: isLoadingProjectActivitySummary,
    data: projectActivitySummary,
    isError: isErrorLoadingActivitySummary,
  } = useDocumentActivitySummary(documentId || '');

  const { data: involvedCompanies, isLoading: isCompaniesInvolvedLoading } = useQuery(
    ['get-companies-invoked-in-document', documentId],
    async (): Promise<ProjectCompany[]> => {
      const result = await DocumentsAPI.getCompaniesInvolvedInDocument(documentId);

      return result.data || [];
    },
    { refetchOnWindowFocus: false },
  );

  const { isLoading: isLoadingProjectEvents, data: projectEvents } = useQuery(
    ['get-project-events-for-document', documentId],
    async () => {
      const result = await DocumentsAPI.getAllDocumentEvents(documentId);

      const data = result?.data;

      // Dedupe Land Title Change events by permitnum
      return dedupeLandTitleChangeEvents(data);
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    projects: projectsList,
    isLoading: isProjectsByAddressLoading,
    count: numberOfProjectsByAddress,
    setPage,
  } = useProjectsByAddress({});

  const { data: landTitleHistory, isLoading: isLoadingLandTitleHistory } =
    useDocumentLandTitleValue(documentId!);

  return {
    project,
    isLoadingProject,
    projectActivitySummary,
    isLoadingProjectActivitySummary,
    isErrorLoadingActivitySummary,
    involvedCompanies,
    isCompaniesInvolvedLoading,
    projectEvents,
    isLoadingProjectEvents,
    projectsList,
    isProjectsByAddressLoading,
    numberOfProjectsByAddress,
    setPage,
    landTitleHistory,
    isLoadingLandTitleHistory,
  };
};
